<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InternalConversationsAPI from 'dashboard/api/internalConversations';
import InternalGroupsAPI from 'dashboard/api/internalGroups';
import InternalNewChatModal from './InternalNewChatModal.vue';
import InternalNewGroupModal from './InternalNewGroupModal.vue';
import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';

const props = defineProps({
  inboxId: {
    type: [String, Number],
    required: true,
  },
  conversationId: {
    type: [String, Number],
    default: 0,
  },
  showConversationList: {
    type: Boolean,
    default: true,
  },
  isOnExpandedLayout: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['conversationLoad']);

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const isLoading = ref(false);
const isLoadingMore = ref(false);
const showNewChatModal = ref(false);
const showNewGroupModal = ref(false);
const isCreating = ref(false);
const isCreatingGroup = ref(false);
const currentPage = ref(0);
const allCount = ref(0);
const listRef = ref(null);

const chatList = useMapGetter('getAllConversations');
const accountId = useMapGetter('getCurrentAccountId');
const inbox = useMapGetter('inboxes/getInbox');

const currentInbox = computed(() => inbox.value(props.inboxId) || {});
const pageTitle = computed(() => currentInbox.value.name || '');
const conversationList = computed(() => chatList.value);
const hasMore = computed(() => conversationList.value.length < allCount.value);
const showEndOfList = computed(
  () => !hasMore.value && conversationList.value.length > 0
);

const intersectionObserverOptions = computed(() => ({
  root: listRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

async function loadConversations({ reset = false } = {}) {
  if (!props.inboxId) return;

  if (reset) {
    if (isLoading.value) return;
    isLoading.value = true;
    currentPage.value = 0;
    allCount.value = 0;
    store.dispatch('setActiveInbox', props.inboxId);
    store.dispatch('emptyAllConversations');
  } else {
    if (isLoadingMore.value || isLoading.value || !hasMore.value) return;
    isLoadingMore.value = true;
  }

  const page = reset ? 1 : currentPage.value + 1;

  try {
    const { data } = await InternalConversationsAPI.get(props.inboxId, {
      page,
    });
    const conversations = data?.data?.payload || [];
    const meta = data?.data?.meta || {};

    allCount.value = meta.all_count ?? conversations.length;
    currentPage.value = meta.current_page ?? page;

    conversations.forEach(conversation => {
      store.dispatch('addConversation', conversation);
    });

    if (reset) {
      emit('conversationLoad');
    }
  } catch (error) {
    useAlert(t('CONVERSATION.INTERNAL_MESSAGING.ERROR_LOAD'));
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }
}

function loadMoreConversations() {
  loadConversations({ reset: false });
}

function navigateToConversation(conversationId) {
  const path = frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: props.inboxId,
      id: conversationId,
    })
  );
  router.push({ path });
}

async function onCreateGroup({ name, participantIds }) {
  showNewGroupModal.value = false;
  isCreatingGroup.value = true;

  try {
    const { data: conversation } = await InternalGroupsAPI.create({
      inboxId: props.inboxId,
      name,
      participantIds,
    });

    store.dispatch('addConversation', conversation);
    navigateToConversation(conversation.id);
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('CONVERSATION.INTERNAL_MESSAGING.ERROR_CREATE_GROUP');
    useAlert(message);
  } finally {
    isCreatingGroup.value = false;
  }
}

async function onSelectAgent(agent) {
  showNewChatModal.value = false;
  isCreating.value = true;

  try {
    const { data: conversation } = await InternalConversationsAPI.create({
      inboxId: props.inboxId,
      recipientId: agent.id,
    });

    store.dispatch('addConversation', conversation);
    navigateToConversation(conversation.id);
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('CONVERSATION.INTERNAL_MESSAGING.ERROR_CREATE');
    useAlert(message);
  } finally {
    isCreating.value = false;
  }
}

watch(
  () => props.inboxId,
  () => {
    loadConversations({ reset: true });
  }
);

onMounted(() => {
  loadConversations({ reset: true });
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 conversations-list-wrap bg-n-surface-1"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : 'w-[340px] 2xl:w-[412px]',
    ]"
  >
    <div
      class="flex items-center justify-between gap-2 px-4 py-3 border-b border-n-weak"
    >
      <h1 class="text-base font-medium text-n-slate-12 truncate">
        {{ pageTitle }}
      </h1>
      <div class="flex items-center gap-1">
        <NextButton
          v-tooltip.top="$t('CONVERSATION.INTERNAL_MESSAGING.NEW_MESSAGE')"
          icon="i-lucide-plus"
          xs
          faded
          slate
          :is-loading="isCreating"
          @click="showNewChatModal = true"
        />
        <NextButton
          v-tooltip.top="$t('CONVERSATION.INTERNAL_MESSAGING.CREATE_GROUP')"
          icon="i-lucide-users"
          xs
          faded
          slate
          :is-loading="isCreatingGroup"
          @click="showNewGroupModal = true"
        />
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center items-center p-8">
      <Spinner />
    </div>

    <p
      v-else-if="!conversationList.length"
      class="flex overflow-auto justify-center items-center p-6 text-center text-n-slate-11 text-sm"
    >
      {{ $t('CONVERSATION.INTERNAL_MESSAGING.EMPTY_STATE') }}
    </p>

    <div v-else ref="listRef" class="overflow-y-auto flex-1">
      <ConversationCard
        v-for="chat in conversationList"
        :key="chat.id"
        :chat="chat"
        hide-inbox-name
        compact
      />
      <div v-if="isLoadingMore" class="flex justify-center my-4">
        <Spinner class="text-n-brand" />
      </div>
      <p
        v-else-if="showEndOfList"
        class="p-4 text-center text-n-slate-11 text-sm"
      >
        {{ $t('CHAT_LIST.EOF') }}
      </p>
      <IntersectionObserver
        v-else
        :options="intersectionObserverOptions"
        @observed="loadMoreConversations"
      />
    </div>

    <InternalNewChatModal
      :show="showNewChatModal"
      :inbox-id="inboxId"
      @close="showNewChatModal = false"
      @select="onSelectAgent"
    />
    <InternalNewGroupModal
      :show="showNewGroupModal"
      :inbox-id="inboxId"
      @close="showNewGroupModal = false"
      @create="onCreateGroup"
    />
  </div>
</template>
